/**
 * ConfigDiscordPage — admin configura webhook Discord + eventi notificati (FASE 6 iter 6).
 *
 * Singleton ConfigurazioneLega. PUT richiede ruolo PRESIDENTE_LEGA.
 */

import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";
interface ConfigItem {
  id: string;
  nome: string;
  discord_webhook_url: string | null;
  notifiche_eventi_attivi: string[];
}

const EVENTI_LABEL: Record<string, string> = {
  nuova_offerta: tr("💼 Nuova offerta"),
  offerta_accettata: tr("✅ Offerta accettata"),
  offerta_rifiutata: tr("❌ Offerta rifiutata"),
  controproposta_emessa: tr("🔄 Controproposta"),
  offerta_auto_rifiutata: "⛔ Auto-rifiutata",
  acquisto_svincolato_registrato: tr("🆕 Svincolato"),
  giornata_calcolata: tr("📊 Giornata calcolata"),
  giornata_annullata: tr("⚠️ Giornata annullata"),
  finestra_mercato_aperta: tr("🏪 Finestra aperta"),
  finestra_mercato_chiusa: tr("🚪 Finestra chiusa"),
  abbandono_serie_a: tr("🛫 Abbandono SA"),
  scraper_voti_fallito: tr("🔴 Scraper voti KO"),
  scraper_tm_fallito: tr("🔴 Scraper TM KO"),
};

export function ConfigDiscordPage() {
  useAuth(); // session check
  const [nome, setNome] = useState("");
  const [webhook, setWebhook] = useState("");
  const [eventi, setEventi] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testEvento, setTestEvento] = useState("nuova_offerta");

  useEffect(() => {
    api
      .get<ConfigItem | null>("/admin/configurazione-lega")
      .then((r) => {
        const c = r.data;
        if (c) {
          setNome(c.nome);
          setWebhook(c.discord_webhook_url ?? "");
          setEventi(new Set(c.notifiche_eventi_attivi));
        } else {
          setNome("FantaSquad");
          setEventi(new Set(Object.keys(EVENTI_LABEL)));
        }
        setLoading(false);
      })
      .catch((e) => {
        const err = e as { response?: { data?: { detail?: string } }; message?: string };
        setErrore(err.response?.data?.detail ?? err.message ?? tr("errore caricamento"));
        setLoading(false);
      });
  }, []);

  function toggleEvento(e: string) {
    const next = new Set(eventi);
    if (next.has(e)) next.delete(e);
    else next.add(e);
    setEventi(next);
  }

  async function salva() {
    setSubmitting(true);
    setErrore(null);
    setSuccess(null);
    try {
      await api.put<ConfigItem>("/admin/configurazione-lega", {
        nome,
        discord_webhook_url: webhook.trim() || null,
        notifiche_eventi_attivi: Array.from(eventi),
      });
      setSuccess(tr("Configurazione salvata"));
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    } finally {
      setSubmitting(false);
    }
  }

  async function testWebhook() {
    setSubmitting(true);
    setErrore(null);
    setSuccess(null);
    try {
      const r = await api.post("/admin/discord/test", {
        evento_tipo: testEvento,
        payload: { test: true, timestamp: new Date().toISOString() },
        webhook_url: webhook || null,
      });
      const stats = r.data.stats;
      setSuccess(
        tr("Test inviato: {{v1}}/{{v2}} OK ({{v3}} falliti)", {
          v1: stats.successi,
          v2: stats.processati,
          v3: stats.falliti,
        }),
      );
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-bg text-fg p-6">{tr("Caricamento…")}</div>;
  }

  return (
    <MobileShell logoSuffix="DISCORD">
      <h2 className="font-display text-3xl text-accent tracking-wide">
        {tr("Configurazione Discord")}
      </h2>

      <div className="card space-y-3">
        <div>
          <label className="text-xs text-fg-muted uppercase tracking-wider">
            {tr("Nome lega")}
          </label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
        <div>
          <label className="text-xs text-fg-muted uppercase tracking-wider">
            {tr("Discord webhook URL")}
          </label>
          <input
            type="text"
            value={webhook}
            onChange={(e) => setWebhook(e.target.value)}
            placeholder={"https://discord.com/api/webhooks/123/abc..."}
            className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      <div className="card">
        <h3 className="font-bold text-accent mb-2">{tr("Eventi notificati su Discord")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(EVENTI_LABEL).map(([k, v]) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={eventi.has(k)} onChange={() => toggleEvento(k)} />
              <span>{v}</span>
            </label>
          ))}
        </div>
      </div>

      {errore && (
        <div className="card bg-danger/10 border-danger/30 text-sm text-danger">{errore}</div>
      )}
      {success && (
        <div className="card bg-success/10 border-success/30 text-sm text-success">{success}</div>
      )}

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={salva}
          disabled={submitting}
          className="btn-primary text-sm disabled:opacity-50"
        >
          {submitting ? tr("Salvataggio…") : tr("💾 Salva configurazione")}
        </button>
        <select
          value={testEvento}
          onChange={(e) => setTestEvento(e.target.value)}
          className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
        >
          {Object.entries(EVENTI_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <button
          onClick={testWebhook}
          disabled={submitting || !webhook}
          className="btn-ghost text-sm disabled:opacity-50"
        >
          {tr("🧪 Test webhook")}
        </button>
      </div>
    </MobileShell>
  );
}
